import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { HttpErrorResponse } from '@angular/common/http'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CallerService } from '../services/caller.service'
import { PpaActionsService } from '../services/ppa-actions.service'
import { PpaModel, ReviewModel } from '../ngrx-store/app.state'
import { SeoService } from '../services/seo.service'
import { RETURN_URL_QUERY_PARAM } from '../utils/return-url.util'
import { housesNearPpaQueryParams, ppaPlace, ppaTypeIcon } from '../utils/ppa.util'
import { PpaReviewPager } from '../utils/ppa-review-pager'

/** One PPA (/ppa/:id) - a shared link, so logged out too. Laid out like the house and sale pages. */
@Component({
  selector: 'app-public-ppa',
  templateUrl: './public-ppa.component.html',
  styleUrls: ['./public-ppa.component.css'],
})
export class PublicPpaComponent implements OnInit, OnDestroy {
  ppa?: PpaModel
  isLoading = true
  notFound = false
  loadError = false
  postingReview = false
  readonly ppaPlace = ppaPlace

  /** Its reviews, newest first - the first page comes with the PPA, "Show more reviews" fetches the rest. */
  readonly reviewPager = new PpaReviewPager((before) =>
    this.callerService.getPpaReviews(this.ppa?.slug_id ?? String(this.ppa?.id), before))

  private destroy$ = new Subject<void>()
  private destroyed = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public callerService: CallerService,
    public ppaActions: PpaActionsService,
    private seo: SeoService,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit() {
    this.loadPpa()
  }

  ngOnDestroy() {
    this.destroyed = true
    this.destroy$.next()
    this.destroy$.complete()
  }

  loadPpa() {
    this.isLoading = true
    this.notFound = false
    this.loadError = false

    const id = this.route.snapshot.paramMap.get('id')
    this.callerService.getPublicPpa(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res.body?.ppa) {
          this.ppa = res.body.ppa
          this.reviewPager.start(res.body.ppa.Reviews, res.body.ppa.reviews_next_cursor)
          this.updateSeo(res.body.ppa)
        } else {
          this.showNotFound()
        }
        this.isLoading = false
      },
      error: (err: HttpErrorResponse) => {
        const mergedInto = err.status === 404 ? err.error?.merged_into?.slug_id : null
        if (mergedInto) {
          this.openMergedInto(mergedInto)
          return
        }
        // Only a real 404 keeps it out of search - a blip while Google crawls shouldn't drop a PPA from results.
        if (err.status === 404) this.showNotFound()
        else this.loadError = true
        this.isLoading = false
      },
    })
  }

  /**
   * It was a duplicate, merged into another PPA (its reviews are there now): go there, in place of
   * this link in the history. The router keeps this component, so load the other PPA once it's there.
   */
  private openMergedInto(slugId: string) {
    this.router.navigate(['/ppa', slugId], { replaceUrl: true, queryParamsHandling: 'preserve' }).then((navigated) => {
      if (navigated && !this.destroyed) this.loadPpa()
    })
  }

  /** Write a review - and once it's in, it's on the page, with the numbers it changed. */
  writeReview() {
    const ppa = this.ppa
    if (!ppa || this.postingReview) return
    this.ppaActions.review(ppa, () => (this.postingReview = true)).subscribe({
      next: ({ review, ppa_stats }) => {
        if (review) this.reviewPager.prepend(review)
        this.ppa = {
          ...ppa,
          ...(ppa_stats && { review_count: Number(ppa_stats.review_count), avg_rating: ppa_stats.avg_rating }),
          ...(ppa_stats?.rating_breakdown && { rating_breakdown: ppa_stats.rating_breakdown }),
        }
      },
      complete: () => (this.postingReview = false),
    })
  }

  get place(): string {
    return ppaPlace(this.ppa?.Locations?.[0])
  }

  get typeIcon(): string {
    return ppaTypeIcon(this.ppa?.type_of_ppa)
  }

  get reviews(): ReviewModel[] {
    return this.reviewPager.reviews
  }

  get reviewCount(): number {
    return this.ppa?.review_count ?? this.reviews.length
  }

  /**
   * From the backend's stats (GET /ppas/:id sends them): each corp member counted once, by their
   * latest rated review - the same number the PPA's card shows. Averaging every review here would
   * count someone who reviewed five times five times.
   */
  get averageRating(): number {
    if (this.ppa?.avg_rating !== undefined) {
      return this.ppa.avg_rating === null ? 0 : Number(this.ppa.avg_rating) // MySQL decimals arrive as strings
    }
    const rated = this.reviews.filter((r) => r.star_rating !== null)
    if (!rated.length) return 0
    return rated.reduce((acc, r) => acc + (r.star_rating ?? 0), 0) / rated.length
  }

  get averageRatingDisplay(): string {
    return this.averageRating > 0 ? this.averageRating.toFixed(1) : '-'
  }

  /**
   * How many ratings of each number of stars, 5 down to 1 - the bars beside the average. From the
   * backend's `rating_breakdown` (all of them, counted like the average), since only a page of the
   * reviews is loaded; from the loaded reviews where it isn't sent.
   */
  get ratingBreakdown(): Array<{ stars: 1 | 2 | 3 | 4 | 5; count: number; percent: number }> {
    const fromServer = this.ppa?.rating_breakdown
    const rated = this.reviews.filter((r) => r.star_rating)
    const countOf = (stars: 1 | 2 | 3 | 4 | 5) =>
      fromServer ? Number(fromServer[stars] ?? 0) : rated.filter((r) => Math.round(r.star_rating ?? 0) === stars).length
    const rows = ([5, 4, 3, 2, 1] as const).map((stars) => ({ stars, count: countOf(stars) }))
    const total = rows.reduce((sum, row) => sum + row.count, 0)
    return rows.map((row) => ({ ...row, percent: total ? Math.round((row.count / total) * 100) : 0 }))
  }

  /** Whether there are any ratings to draw bars for. */
  get hasRatings(): boolean {
    return this.ratingBreakdown.some((row) => row.count > 0)
  }

  /** After "Show more reviews": focus the first new one, so a keyboard or screen reader picks up from there. */
  focusReview(added: ReviewModel[]): void {
    if (!added.length) return
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(`[data-review-id="${added[0].id}"]`)?.focus())
  }

  /** Five stars, filled up to the rating (rounded to a half). */
  starsFor(rating: number | null): Array<'star' | 'star_half' | 'star_border'> {
    const r = Math.round((rating ?? 0) * 2) / 2
    return Array.from({ length: 5 }, (_, i) => (i + 1 <= r ? 'star' : i + 0.5 === r ? 'star_half' : 'star_border'))
  }

  /** Anonymous reviews have no CorpMember (the backend redacts them). */
  reviewerName(review: ReviewModel): string {
    return review.CorpMember?.first_name ?? review.CorpMember?.nickname ?? 'A corps member'
  }

  reviewerLink(review: ReviewModel): string[] | null {
    return review.CorpMember?.slug_id ? ['/p', review.CorpMember.slug_id] : null
  }

  get corperCount(): number {
    return this.ppa?.corper_count ?? 0
  }

  get housesNearby(): number {
    return this.ppa?.houses_nearby_count ?? 0
  }

  get housesQueryParams(): Record<string, string> {
    return this.ppa ? housesNearPpaQueryParams(this.ppa) : {}
  }

  /** Logged out: sign up, then come back to this PPA. */
  get signupQueryParams(): Record<string, string> {
    return { [RETURN_URL_QUERY_PARAM]: this.router.url.split(/[?#]/)[0] }
  }

  trackById = (_index: number, review: ReviewModel) => review.id

  private showNotFound() {
    this.notFound = true
    this.seo.setPage({ title: 'Not found', path: this.router.url, noindex: true })
  }

  private updateSeo(ppa: PpaModel) {
    const where = ppaPlace(ppa.Locations?.[0])
    const reviews = ppa.review_count ?? ppa.Reviews?.length ?? 0
    this.seo.setPage({
      title: `${ppa.name}${where ? `, ${where}` : ''} – PPA reviews`,
      description:
        `${ppa.name} (${ppa.type_of_ppa})${where ? ` in ${where}` : ''}: ` +
        (reviews ? `${reviews} review${reviews === 1 ? '' : 's'} from corps members` : 'what corps members say') +
        ` about serving here, on corpers.ng.`,
      image: ppa.Media?.[0]?.url,
      path: this.router.url,
    })
  }
}
